import type {
  KnowledgeProgress,
  KnowledgeSetupFailure,
  KnowledgeSetupResult,
} from './run-knowledge-setup';

export type Translate = (
  key: string,
  options?: Record<string, unknown>
) => string;

/** The progress toast. It never closes by itself (`autoClose: false`). */
export const PROGRESS_TOAST_ID = 'bobcat-knowledge-setup';

const K = 'pro-studio/homepage:knowledge_setup';
export const KEYS = {
  progressHeader: `${K}.progress_header`,
  successHeader: `${K}.success_header`,
  failureHeader: `${K}.failure_header`,
  keysAdded: `${K}.keys_added`,
  corpusBuilt: `${K}.corpus_built`,
  keysPartial: `${K}.keys_partial`,
  corpusUnused: `${K}.corpus_unused`,
  searchNodeMissing: `${K}.search_node_missing`,
  solutionChanged: `${K}.solution_changed`,
  solutionLeft: `${K}.solution_left`,
  uploadFailed: `${K}.upload_failed`,
  progressAddingKey: `${K}.progress_adding_key`,
  progressCreatingCorpus: `${K}.progress_creating_corpus`,
  progressAddingDocument: `${K}.progress_adding_document`,
  progressPointingSearch: `${K}.progress_pointing_search`,
} as const;

/** The cause as a sentence: a cause that already ends in punctuation is kept. */
const endSentence = (cause: string) =>
  /[.!?]$/.test(cause) ? cause : `${cause}.`;

/** The first sentence: why the run stopped. */
const leadSentence = (failure: KnowledgeSetupFailure, t: Translate) => {
  if (failure.reason === 'search-node-missing') {
    return t(KEYS.searchNodeMissing);
  }
  if (failure.reason === 'solution-changed') {
    return t(KEYS.solutionChanged);
  }
  if (failure.reason === 'solution-left') {
    return t(KEYS.solutionLeft);
  }
  if (failure.failedUpload) {
    return t(KEYS.uploadFailed, { name: failure.failedUpload });
  }
  return endSentence(failure.cause);
};

/**
 * The failure, followed by what the run had already done when it stopped.
 * Each sentence is one translation key (plurals by `count`), with one
 * exception, the LIMITATION of this function: a `step-failed` that is not a
 * script upload leads with its `cause` untranslated. That is whatever the
 * failing step threw (see `KnowledgeSetupFailure.cause`): usually a `detail`
 * from `/ai/knowledge/*` or a transport error, but a failed read of the search
 * script throws this app's own English sentence (`fetchVersionFileContent`).
 * A failed upload leads with `upload_failed` naming the file, and the three
 * reasons this app raises itself lead with their own sentence (`leadSentence`).
 */
export const buildFailureText = (
  failure: KnowledgeSetupFailure,
  t: Translate
): string => {
  const parts = [leadSentence(failure, t)];
  if (failure.keysAdded < failure.keysTotal) {
    parts.push(
      t(KEYS.keysPartial, {
        count: failure.keysTotal,
        added: failure.keysAdded,
      })
    );
  }
  if (failure.corpusId) {
    parts.push(
      t(KEYS.corpusUnused, {
        count: failure.documentsTotal,
        documents: failure.documents,
        project: failure.projectId,
        corpus: failure.corpusId,
      })
    );
  }
  return parts.join(' ');
};

export const buildSuccessText = (
  result: KnowledgeSetupResult,
  t: Translate
): string => {
  const parts = [t(KEYS.keysAdded, { count: result.keysAdded })];
  if (result.corpusId) {
    parts.push(
      t(KEYS.corpusBuilt, {
        count: result.documents,
        project: result.projectId,
      })
    );
  }
  return parts.join(' ');
};

export const buildProgressText = (
  progress: KnowledgeProgress,
  t: Translate
): string => {
  switch (progress.step) {
    case 'adding-key':
      return t(KEYS.progressAddingKey, { name: progress.name });
    case 'creating-corpus':
      return t(KEYS.progressCreatingCorpus, { project: progress.project });
    case 'adding-document':
      return t(KEYS.progressAddingDocument, { name: progress.name });
    case 'pointing-search':
      return t(KEYS.progressPointingSearch);
  }
};

export interface ProgressDeps {
  t: Translate;
  /** The progress toast is on screen now (`toast.isActive`). */
  isActive: (toastId: string) => boolean;
  send: (toast: {
    type: 'info';
    header: string;
    text: string;
    toastId: string;
    autoClose: false;
  }) => void;
  update: (toast: { toastId: string; header: string; text: string }) => void;
}

/**
 * Shows one progress step. The first step opens the toast; every later one
 * UPDATES it, because react-toastify drops a new toast whose id is still
 * active (`buildToast`: `c.has(id) && updateId == null`), which is what lost
 * every step after the first.
 */
export const reportProgress = (
  progress: KnowledgeProgress,
  { t, isActive, send, update }: ProgressDeps
) => {
  const header = t(KEYS.progressHeader);
  const text = buildProgressText(progress, t);
  if (isActive(PROGRESS_TOAST_ID)) {
    update({ toastId: PROGRESS_TOAST_ID, header, text });
  } else {
    send({
      type: 'info',
      header,
      text,
      toastId: PROGRESS_TOAST_ID,
      autoClose: false,
    });
  }
};

export interface OutcomeDeps {
  t: Translate;
  send: (toast: {
    type: 'success' | 'error';
    header: string;
    text: string;
    toastId: string;
    autoClose: number | false;
  }) => void;
  dismiss: (toastId: string) => void;
  /** Makes the final toast's id unique to this solution. */
  solutionKey: string;
}

/**
 * Replaces the progress toast with the final one. Both steps are needed.
 * react-toastify drops a toast whose id is still active (`buildToast`:
 * `c.has(id) && updateId == null`), so the final toast gets its own id; and
 * the progress toast has `autoClose: false`, so without the dismiss it would
 * stay on screen next to the result.
 */
export const reportSetupOutcome = (
  outcome:
    | { result: KnowledgeSetupResult }
    | { failure: KnowledgeSetupFailure },
  { t, send, dismiss, solutionKey }: OutcomeDeps
) => {
  dismiss(PROGRESS_TOAST_ID);
  const toastId = `${PROGRESS_TOAST_ID}-result-${solutionKey}`;
  if ('failure' in outcome) {
    send({
      type: 'error',
      header: t(KEYS.failureHeader),
      text: buildFailureText(outcome.failure, t),
      toastId,
      autoClose: false,
    });
  } else {
    send({
      type: 'success',
      header: t(KEYS.successHeader),
      text: buildSuccessText(outcome.result, t),
      toastId,
      autoClose: 8000,
    });
  }
};
